import React, { useState } from 'react';
import Dialog from '@dashboard/components/Dialog';
import { Form } from '@dashboard/components/Formik';
import Tooltip from '@dashboard/components/Tooltip';
import clsx from 'clsx';
import { XCircleIcon } from '@heroicons/react/24/outline';
import Link from '@/dashboard/components/Link';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { fetcher } from '@dashboard/services/request';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { toast } from '@dashboard/context/ToastContext';
import { subscriptionKey } from '@dashboard/lib/queryKeys';
import { format } from 'date-fns';

const PaymentsSubscriptionCancelDialog = ({ subscription }) => {
  const [open, setOpen] = useState(false);
  const { token } = useAuth();
  const queryClient = useQueryClient();

  const cancelSubscription = useMutation({
    mutationFn: values =>
      fetcher(`/sellers/${subscription.seller}/subscriptions/${subscription._id}/cancel`, token, {
        method: 'POST',
        body: JSON.stringify(values),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subscriptionKey(subscription._id) });
      toast({
        title: 'Subscription canceled',
        type: 'success',
      });

      setOpen(false);
    },
    onError: error => {
      toast({
        title: 'An error occurred while canceling the subscription',
        description: error?.message,
        type: 'error',
      });
    },
  });

  const tooltipLabel =
    subscription.status === 'canceled'
      ? 'This subscription has been canceled'
      : subscription.status === 'failed'
        ? 'This subscription failed'
        : !subscription.subscriptionId || subscription.status === 'incomplete_expired'
          ? 'This subscription never started'
          : '';

  const hasSubscriptionSchedule = (subscription?.planIterations ?? 0) > 0;

  let cancelSettingsOptions = [{ label: 'Immediately', value: 'cancel_immediately' }];

  if (!hasSubscriptionSchedule) {
    cancelSettingsOptions = [
      ...cancelSettingsOptions,
      {
        label: 'End of the current period',
        value: 'cancel_at_period_end',
      },
      { label: 'On a custom date', value: 'cancel_at' },
    ];
  }

  const description = hasSubscriptionSchedule
    ? 'Cancels immediately.'
    : subscription.cancelAt
      ? `Ends on ${format(new Date(subscription.cancelAt), 'd MMM, yyyy')}.`
      : '';

  return (
    <>
      <Tooltip triggerClassName="w-fit" label={tooltipLabel} side="right">
        <Link
          onClick={() => setOpen(true)}
          className={clsx('w-full flex gap-x-2 items-center no-underline', {
            'pointer-events-none opacity-50 cursor-not-allowed':
              subscription.status === 'canceled' ||
              subscription.status === 'failed' ||
              !subscription.subscriptionId ||
              subscription.status === 'incomplete_expired',
          })}
        >
          <>
            <XCircleIcon className="w-4 h-4" />
            Cancel subscription
          </>
        </Link>
      </Tooltip>

      <Dialog
        title="Cancel subscription"
        description={description}
        cancelButton={null}
        confirmButton={null}
        size="md"
        open={open}
        onCancel={() => setOpen(false)}
      >
        {() => (
          <Form
            showState={false}
            initialValues={{
              reason: '',
              cancelSettings: 'cancel_immediately',
              sendCancellationEmail: true,
            }}
            onSubmit={async values => {
              try {
                const cancelChoice = (() => {
                  if (values.cancelSettings === 'cancel_immediately') {
                    return {
                      cancelImmediately: true,
                    };
                  }
                  if (values.cancelSettings === 'cancel_at') {
                    return {
                      cancelAt: values.cancelAt,
                    };
                  }

                  return {
                    cancelAtPeriodEnd: true,
                  };
                })();

                let data = await cancelSubscription.mutateAsync({
                  reason: values.reason,
                  ...cancelChoice,
                  sendCancellationEmail: values.sendCancellationEmail === true,
                });

                return {
                  data,
                };
              } catch (error) {
                return {
                  error,
                };
              }
            }}
            onSuccess={() => {
              queryClient.invalidateQueries({ queryKey: subscriptionKey(subscription._id) });

              toast({
                title: 'Subscription canceled',
                type: 'success',
              });

              setOpen(false);
            }}
            fields={({ values }) => [
              {
                name: 'reason',
                label: 'Reason for cancellation',
                tags: ['not shown to customer'],
                type: 'textarea',
              },
              {
                name: 'cancelSettings',
                label: 'Cancellation date',
                type: 'radio',
                reduceMargin: values.cancelSettings === 'cancel_at' ? true : false,
                options: cancelSettingsOptions,
                visible: !hasSubscriptionSchedule,
              },
              {
                name: 'cancelAt',
                label: 'Cancel on a custom date',
                type: 'date',
                visible: values.cancelSettings === 'cancel_at',
                required: values.cancelSettings === 'cancel_at',
              },
              {
                name: 'sendCancellationEmail',
                label: 'Send cancellation email to customer',
                type: 'checkbox',
              },
            ]}
            formSubmitAlign="end"
            submitButton={{
              label: 'Cancel subscription',
              appearance: 'primary',
            }}
          />
        )}
      </Dialog>
    </>
  );
};

export default PaymentsSubscriptionCancelDialog;
